/* eslint-disable no-unused-vars */
import { useState, useCallback, memo, useMemo, useEffect, createRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Accordion, AccordionDetails } from '@mui/material';
import {
  GeneralTargetingSection,
  GeneralTrackingSection,
  GeneralCreativeSection,
  CampaignSection,
  CampaignSectionAction,
} from 'components';
import { useLocation, useNavigate } from 'react-router-dom';
import { campaignEnvironment } from 'constants';
import CampaignName from 'components/campaign/generalSection/campaignNameSection/CampaignName';
import CampaignType from 'components/campaign/generalSection/CampaignType';
import CampaignGoal from 'components/campaign/generalSection/CampaignGoal';
import SchedulerSection from 'components/campaign/generalSection/schedulerSection/SchedulerSection';
import ApplicationSection from 'components/campaign/applicationSection/ApplicationSection.js';
import BiddingSection from 'components/campaign/biddingSection/BiddingSection';
import Footer from './Footer';
import { checkCurrentCampaignObj } from 'utils';
import {
  formErrorsCampaign_action,
  generalActions,
  trackingActions,
  creativesActions,
} from 'store/actions';
import * as S from './CampaignMain.styled';

const scrollToCreativeSection = () => {
  const elem = document.getElementById('creativeSectionId');
  let value = 0;
  const intervalId = setInterval(() => {
    const rect = elem.getBoundingClientRect();
    value += 10;
    window.scrollTo(0, value);
    if (rect.top <= 200) {
      clearInterval(intervalId);
    }
  }, 1);
};

const CampaignMain = ({ environment }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const errorFlag = useSelector((state) => state.formErrorsCampaign);

  const isOnBoarding = useSelector(
    (state) => state.general.mainCampaignObject?.currentCampaignObj?.isOnBoarding,
  );

  const [expanded, setExpanded] = useState('main');

  const setTrueErrorField = (pannel) => {
    dispatch(formErrorsCampaign_action.updateErrorField({ errorField: pannel, newValue: true }));
  };

  const location = useLocation();
  const navigate = useNavigate();
  const previousPathRef = useRef(location.pathname);

  const handleChange = useCallback(
    (panel) => (isExpanded) => {
      if (!checkCurrentCampaignObj(expanded, setTrueErrorField, panel, setExpanded, environment))
        return;
      if (expanded === panel) {
        setExpanded(false);
        return;
      }
      setExpanded(isExpanded ? panel : false);
    },
    [expanded],
  );
  const isOnBoardingFlag = useMemo(
    () => (isOnBoarding === '' || isOnBoarding === undefined ? false : !isOnBoarding),
    [isOnBoarding],
  );

  const expand_sections = useMemo(() => {
    if (environment === campaignEnvironment.edit) {
      return true;
    }
    if (environment === campaignEnvironment.duplicate) {
      return true;
    }
  }, [environment]);

  useEffect(() => {
    let timerId;
    if (expand_sections) {
      const sectionToOpen = JSON.parse(localStorage.getItem('sectionToOpen'));
      if (sectionToOpen) {
        setExpanded(sectionToOpen);
        scrollToCreativeSection();
        timerId = setTimeout(() => {
          localStorage.removeItem('sectionToOpen');
        }, 1500);
      } else {
        setExpanded('general');
      }
      return () => clearTimeout(timerId);
    }
  }, [expand_sections]);

  return (
    <>
      <CampaignSection
        error={errorFlag.main}
        expanded={expand_sections ? true : expanded === 'main'}
        handleChange={handleChange('main')}
        title={t('campaign.application.title')}
        details={<ApplicationSection environment={environment} />}
        actions={
          <CampaignSectionAction
            onClick={handleChange('general')}
            title='Go to General information'
            expanded={expanded === 'main'}
            hidden={
              environment === campaignEnvironment.edit ||
              environment === campaignEnvironment.duplicate
            }
          />
        }
      />
      <CampaignSection
        error={errorFlag.campaign_type || errorFlag.campaign_name || errorFlag.general}
        expanded={expand_sections ? true : expanded === 'general'}
        handleChange={handleChange('general')}
        title={t('campaign.general_information.title')}
        details={
          <S.GeneralAccContainer>
            {/* <Accordion expanded={isOnBoarding !== ''}> */}
            {/* <CampaignType environment={environment} /> */}
            <AccordionDetails>
              <Accordion expanded={isOnBoardingFlag}>
                <CampaignName
                  environment={environment}
                  expanded={expand_sections ? true : expanded === 'general'}
                />
                <AccordionDetails>
                  <CampaignGoal environment={environment} />
                  <SchedulerSection environment={environment} />
                </AccordionDetails>
              </Accordion>
            </AccordionDetails>
            {/* </Accordion> */}
          </S.GeneralAccContainer>
        }
        actions={
          <CampaignSectionAction
            onClick={handleChange('targeting')}
            title='Go to Targeting'
            expanded={expanded === 'general'}
            hidden={
              environment === campaignEnvironment.edit ||
              environment === campaignEnvironment.duplicate
            }
          />
        }
      />
      <GeneralTargetingSection
        error={errorFlag.targeting}
        environment={environment}
        handleChange={handleChange('targeting')}
        expanded={expanded === 'targeting'}
        setExpanded={setExpanded}
      />
      <CampaignSection
        error={errorFlag.bidding}
        expanded={expanded === 'bidding'}
        isExpandable={true}
        handleChange={handleChange('bidding')}
        title={t('campaign.bidding.title')}
        details={<BiddingSection environment={environment} isOpen={expanded === 'bidding'} />}
        actions={
          <CampaignSectionAction
            onClick={handleChange('creatives')}
            title='Go to Creatives'
            expanded={expanded === 'bidding'}
          />
        }
      />
      <GeneralCreativeSection
        environment={environment}
        expanded={expanded}
        handleChange={handleChange}
      />
      <GeneralTrackingSection
        error={errorFlag.conversionTracking}
        environment={environment}
        expanded={expanded}
        handleChange={handleChange}
      />
      <Footer environment={environment} currentTab={expanded} />
    </>
  );
};

export default memo(CampaignMain);
